// src/features/brain/useBrainQueries.ts
// Phase 6.6 Bundle 3.1 - TanStack Query hooks for Property Brain.
// Bundle 6.7.5 - metadata field on BrainMessage (partial-response flag).
//
// Query keys (architecture v1.4 section 4.3):
//   ['brain-feature-enabled', orgId]   - is brain.query enabled for this org
//   ['brain-conversations', orgId]     - conversation list, newest first
//   ['brain-messages', conversationId] - all messages for one conversation
//
// Pairs with:
//   - useBrainStream.ts (streaming POST to ai-router; not a Query hook)
//   - BrainPanel/BrainChat (consume conversations + messages)

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { useOrgStore } from "@/stores/orgStore";

// === Types =================================================================

export type BrainConversation = {
  id: string;
  organization_id: string;
  title: string;
  created_at: string;
  updated_at: string;
};

export type BrainCitation = {
  entity_type: string;
  entity_id: string;
  label: string;
};

// 6.7.5: metadata carries {partial: true, reason} on assistant messages
// persisted by the client after an abnormal stream end. Null for all
// server-persisted complete messages.
// 6.8: the server persists {truncated: true, stop_reason} when the model
// hit max_tokens; the UI treats truncated like partial for labeling.
export type BrainMessageMetadata = {
  partial?: boolean;
  reason?: string;
  truncated?: boolean;
  stop_reason?: string;
} | null;

export type BrainMessage = {
  id: string;
  conversation_id: string;
  role: "user" | "assistant";
  content: string;
  citations: BrainCitation[] | null;
  metadata: BrainMessageMetadata;
  created_at: string;
};

// === Feature flag check ====================================================
// Reads ai_features (catalog) + organization_ai_settings (per-org overrides)
// and returns whether brain.query is enabled for the current org. Mirrors
// the same check the ai-router Edge Function performs server-side.

export function useBrainFeatureEnabled() {
  const orgId = useOrgStore((s) => s.currentOrgId);

  return useQuery({
    queryKey: ["brain-feature-enabled", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<boolean> => {
      if (!orgId) return false;

      const [{ data: feature }, { data: settings }] = await Promise.all([
        supabase
          .from("ai_features")
          .select("default_enabled")
          .eq("feature_key", "brain.query")
          .maybeSingle(),
        supabase
          .from("organization_ai_settings")
          .select("ai_enabled, feature_overrides")
          .eq("organization_id", orgId)
          .maybeSingle(),
      ]);

      if (!feature) return false;
      const aiEnabled = settings?.ai_enabled ?? true;
      if (!aiEnabled) return false;

      const overrides = (settings?.feature_overrides ?? {}) as Record<
        string,
        boolean
      >;
      const featureEnabled =
        overrides["brain.query"] ?? feature.default_enabled;
      return Boolean(featureEnabled);
    },
  });
}

// === Conversations =========================================================
export type BrainConversationWithCount = BrainConversation & {
  message_count: number;
  first_user_message: string | null;
};

export function useBrainConversations() {
  const orgId = useOrgStore((s) => s.currentOrgId);

  return useQuery({
    queryKey: ["brain-conversations", orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<BrainConversationWithCount[]> => {
      if (!orgId) return [];

      const { data: convs, error } = await supabase
        .from("brain_conversations")
        .select("*")
        .eq("organization_id", orgId)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      if (!convs || convs.length === 0) return [];

      const conversationIds = convs.map((c) => c.id);

      const { data: messages, error: msgErr } = await supabase
        .from("brain_messages")
        .select("conversation_id, role, content, created_at")
        .in("conversation_id", conversationIds)
        .order("created_at", { ascending: true });
      if (msgErr) throw msgErr;

      const countMap = new Map<string, number>();
      const firstUserMessageMap = new Map<string, string>();

      for (const m of (messages ?? []) as Array<{
        conversation_id: string;
        role: string;
        content: string;
        created_at: string;
      }>) {
        countMap.set(m.conversation_id, (countMap.get(m.conversation_id) ?? 0) + 1);
        if (m.role === "user" && !firstUserMessageMap.has(m.conversation_id)) {
          firstUserMessageMap.set(m.conversation_id, m.content);
        }
      }

      return (convs as BrainConversation[]).map((c) => ({
        ...c,
        message_count: countMap.get(c.id) ?? 0,
        first_user_message: firstUserMessageMap.get(c.id) ?? null,
      }));
    },
  });
}

// === Messages ==============================================================

export function useBrainMessages(conversationId: string | null | undefined) {
  return useQuery({
    queryKey: ["brain-messages", conversationId],
    enabled: !!conversationId,
    queryFn: async (): Promise<BrainMessage[]> => {
      if (!conversationId) return [];
      const { data, error } = await supabase
        .from("brain_messages")
        .select("*")
        .eq("conversation_id", conversationId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as BrainMessage[];
    },
  });
}

// === Mutations =============================================================

// Delete a conversation (and all its messages via ON DELETE CASCADE).
// Admin-only per RLS policy in migration 011.
export function useDeleteBrainConversation() {
  const queryClient = useQueryClient();
  const orgId = useOrgStore((s) => s.currentOrgId);

  return useMutation({
    mutationFn: async (conversationId: string): Promise<string> => {
      const { error } = await supabase
        .from("brain_conversations")
        .delete()
        .eq("id", conversationId);
      if (error) throw error;
      return conversationId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["brain-conversations", orgId],
      });
      toast.success("Conversation deleted");
    },
    onError: (err: Error) =>
      toast.error(`Could not delete conversation: ${err.message}`),
  });
}
